import React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/ui/form/form';
import { Input } from '@/shared/ui/input';
import {
  AlertCircle,
  CheckCircle2,
  Clock3,
  Loader2,
  ShieldX,
} from 'lucide-react';
import Navigation from '@/shared/components/home/Navigation';
import {
  getProjectTeamErrorMessage,
  type ApiErrorShape,
} from '../api/api';
import { useTradePartnerApplication } from '../hooks/useProjectTeam';
import type {
  TradePartnerApplication,
  TradePartnerApplicationSubmitInput,
} from '../types/types';

export const tradePartnerApplicationSchema = z
  .object({
    first_name: z.string().trim().min(1, 'First name is required'),
    last_name: z.string().trim().min(1, 'Last name is required'),
    phone_number: z
      .string()
      .trim()
      .regex(/^\d{10}$/, 'Phone number must be exactly 10 digits'),
    job_title: z.string().trim().min(1, 'Job title is required'),
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirm_password: z.string(),
    address: z.string().trim(),
  })
  .refine((values) => values.password === values.confirm_password, {
    path: ['confirm_password'],
    message: 'Passwords do not match',
  });

type ApplicationFormValues = z.infer<typeof tradePartnerApplicationSchema>;

const applicationStatus = (
  application: TradePartnerApplication,
): TradePartnerApplication['status'] => {
  if (
    application.status === 'EXPIRED' ||
    (application.status === 'INVITED' &&
      (application.is_expired ||
        application.is_token_expired ||
        ((application.expires_at || application.token_expires_at) &&
          new Date(application.expires_at || application.token_expires_at!).getTime() <=
            Date.now())))
  ) {
    return 'EXPIRED';
  }
  return application.status;
};

interface StateCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  badge: string;
  destructive?: boolean;
  action?: React.ReactNode;
}

const StateCard: React.FC<StateCardProps> = ({
  icon,
  title,
  description,
  badge,
  destructive,
  action,
}) => (
  <section className="rounded-[1.375rem] border border-border bg-card p-8 text-center text-card-foreground shadow-slate md:p-12">
    <div
      className={`mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full ${
        destructive ? 'bg-destructive/15 text-destructive' : 'bg-primary/15 text-primary'
      }`}
    >
      {icon}
    </div>
    <Badge variant={destructive ? 'destructive' : 'default'} className="mb-4">
      {badge}
    </Badge>
    <h2 className="font-display text-3xl font-extrabold tracking-[-0.02em]">
      {title}
    </h2>
    <p className="mx-auto mt-4 max-w-xl text-base text-muted-foreground">
      {description}
    </p>
    {action && <div className="mt-6">{action}</div>}
  </section>
);

export const TradePartnerApplicationPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';
  const { application, isLoading, error: queryError, submit } =
    useTradePartnerApplication(token);
  const form = useForm<ApplicationFormValues>({
    resolver: zodResolver(tradePartnerApplicationSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      phone_number: '',
      job_title: '',
      password: '',
      confirm_password: '',
      address: '',
    },
  });

  const onSubmit = async (values: ApplicationFormValues) => {
    const payload: TradePartnerApplicationSubmitInput = {
      token,
      ...values,
      address: values.address || undefined,
    };
    try {
      await submit.mutateAsync(payload);
    } catch (error) {
      const apiError = error as ApiErrorShape;
      const fieldErrors = apiError.data?.errors;
      if (fieldErrors) {
        Object.entries(fieldErrors).forEach(([field, messages]) => {
          if (field in values) {
            form.setError(field as keyof ApplicationFormValues, {
              type: 'server',
              message: messages.join(' '),
            });
          }
        });
        return;
      }
      form.setError('root', {
        type: 'server',
        message: getProjectTeamErrorMessage(error, 'Unable to submit application.'),
      });
    }
  };

  const handleNavigate = (sectionId: string) => navigate(`/#${sectionId}`);
  const pageError =
    !token
      ? 'Invalid application link.'
      : queryError
        ? getProjectTeamErrorMessage(queryError, 'Unable to load application.')
        : null;

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <div className="border-b border-border bg-card p-2 md:p-5">
        <Navigation onNavigate={handleNavigate} />
      </div>
      <main className="flex-1 px-4 py-12 sm:px-6">
        <div className="mx-auto max-w-3xl">
          <header className="mb-8 text-center">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              Trade Partner Onboarding
            </p>
            <h1 className="mt-3 font-display text-3xl font-extrabold tracking-[-0.02em] md:text-5xl">
              {application?.company_name ?? 'Application'}
            </h1>
            {application?.project && (
              <p className="mt-3 text-lg text-muted-foreground">
                Project:{' '}
                <span className="font-semibold text-foreground">
                  {application.project.name}
                </span>
              </p>
            )}
          </header>

          {isLoading && token ? (
            <div className="rounded-[1.375rem] border border-border bg-card p-12 text-center shadow-slate">
              <Loader2 className="mx-auto h-10 w-10 animate-spin text-primary" />
              <p className="mt-4 text-muted-foreground">Loading onboarding…</p>
            </div>
          ) : pageError ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{pageError}</AlertDescription>
            </Alert>
          ) : application && applicationStatus(application) === 'SUBMITTED' ? (
            <StateCard
              icon={<CheckCircle2 className="h-9 w-9" />}
              badge="Complete"
              title="Onboarding complete"
              description="Your account is ready. Log in now to access the project."
              action={<Button onClick={() => navigate('/login')}>Log In</Button>}
            />
          ) : application && applicationStatus(application) === 'REJECTED' ? (
            <StateCard
              icon={<ShieldX className="h-9 w-9" />}
              badge="Unavailable"
              title="Onboarding unavailable"
              description="This historical application can no longer be used. Contact the project team for help."
              destructive
            />
          ) : application && applicationStatus(application) === 'EXPIRED' ? (
            <StateCard
              icon={<Clock3 className="h-9 w-9" />}
              badge="Expired"
              title="Application link expired"
              description="This invitation can no longer be used. Ask the project team to resend a fresh application link."
              destructive
            />
          ) : application ? (
            <section className="rounded-[1.375rem] border border-border bg-card p-6 shadow-slate md:p-8">
              <div className="mb-6 grid gap-3 rounded-xl border border-border bg-muted p-5 sm:grid-cols-2">
                <InfoRow label="Company" value={application.company_name} />
                <InfoRow label="Email" value={application.primary_contact_email} />
                <InfoRow label="Project" value={application.project?.name} />
                <InfoRow label="Status" value="Application required" />
              </div>

              {form.formState.errors.root?.message && (
                <Alert variant="destructive" className="mb-5">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    {form.formState.errors.root.message}
                  </AlertDescription>
                </Alert>
              )}

              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <ApplicationField form={form} name="first_name" label="First Name" />
                    <ApplicationField form={form} name="last_name" label="Last Name" />
                    <ApplicationField form={form} name="phone_number" label="Phone Number" />
                    <ApplicationField form={form} name="job_title" label="Job Title" />
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input value={application.primary_contact_email} disabled />
                      </FormControl>
                    </FormItem>
                    <ApplicationField form={form} name="address" label="Company Address" />
                    <ApplicationField
                      form={form}
                      name="password"
                      label="Password"
                      type="password"
                    />
                    <ApplicationField
                      form={form}
                      name="confirm_password"
                      label="Confirm Password"
                      type="password"
                    />
                  </div>

                  <Alert className="border-primary/30 bg-primary/10 text-foreground">
                    <AlertDescription>
                      Your project access becomes active immediately after submitting.
                    </AlertDescription>
                  </Alert>

                  <Button type="submit" className="w-full" disabled={submit.isPending}>
                    {submit.isPending && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Submit Application
                  </Button>
                </form>
              </Form>
            </section>
          ) : null}
        </div>
      </main>
      <footer className="border-t border-border bg-card py-4 text-center text-sm text-muted-foreground">
        &copy; 2026 Ironview 360. All rights reserved.
      </footer>
    </div>
  );
};

interface ApplicationFieldProps {
  form: ReturnType<typeof useForm<ApplicationFormValues>>;
  name: keyof ApplicationFormValues;
  label: string;
  type?: React.HTMLInputTypeAttribute;
  placeholder?: string;
}

const ApplicationField: React.FC<ApplicationFieldProps> = ({
  form,
  name,
  label,
  type,
  placeholder,
}) => (
  <FormField
    control={form.control}
    name={name}
    render={({ field }) => (
      <FormItem>
        <FormLabel>{label}</FormLabel>
        <FormControl>
          <Input {...field} type={type} placeholder={placeholder} />
        </FormControl>
        <FormMessage />
      </FormItem>
    )}
  />
);

const InfoRow: React.FC<{ label: string; value?: string | null }> = ({
  label,
  value,
}) => (
  <div className="flex items-center justify-between gap-4 text-sm">
    <span className="font-medium text-muted-foreground">{label}:</span>
    <span className="text-right font-semibold text-foreground">{value || 'N/A'}</span>
  </div>
);
